import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";

type ApiStatus = "created" | "minted" | "failed" | "unknown";
type Department = "phigora" | "watchcentral";

interface DppItem {
  id: string;
  brand: string;
  model: string;
  serialNumber: string;
  imageUrl?: string;
  customerEmail: string;
  status: ApiStatus;
  department: Department;
  createdAt: string;
  updatedAt: string;
}

interface DppList {
  items: DppItem[];
  total: number;
  page: number;
  limit: number;
}

const STATUS_UI: Record<
  ApiStatus,
  { label: string; variant: "success" | "warning" | "danger" | "mute" }
> = {
  minted: { label: "Verified", variant: "success" },
  created: { label: "Pending", variant: "warning" },
  failed: { label: "Failed", variant: "danger" },
  unknown: { label: "Unknown", variant: "mute" },
};

const LIMIT = 20;

function fmtDate(iso: string) {
  return iso.slice(0, 10).replace(/-/g, ".");
}

function pageRange(current: number, total: number): (number | "…")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= 4) return [1, 2, 3, 4, 5, "…", total];
  if (current >= total - 3)
    return [1, "…", total - 4, total - 3, total - 2, total - 1, total];
  return [1, "…", current - 1, current, current + 1, "…", total];
}

export function DppsPage() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [search, setSearch] = useState("");
  const [brandFilter, setBrandFilter] = useState("All brands");
  const [departmentFilter, setDepartmentFilter] = useState("All departments");
  const [statusFilter, setStatusFilter] = useState("All statuses");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<DppList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, brandFilter, departmentFilter, statusFilter, sortDir]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");

    const params: Record<string, string | number> = {
      page,
      limit: LIMIT,
      sort: sortDir,
    };
    if (debouncedSearch) params.search = debouncedSearch;
    if (brandFilter !== "All brands") params.brand = brandFilter;
    if (departmentFilter !== "All departments")
      params.department = departmentFilter;
    if (statusFilter !== "All statuses") {
      const apiStatus = Object.entries(STATUS_UI).find(
        ([, v]) => v.label === statusFilter
      )?.[0];
      if (apiStatus) params.status = apiStatus;
    }

    api
      .get<DppList>("/admin/dpps", { params })
      .then(({ data: d }) => {
        if (!cancelled) {
          setData(d);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError("Failed to load passports");
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    page,
    debouncedSearch,
    brandFilter,
    departmentFilter,
    statusFilter,
    sortDir,
  ]);

  const totalPages = data ? Math.ceil(data.total / LIMIT) : 0;
  const pages = pageRange(page, totalPages);

  return (
    <div className="adm__body">
      <div className="adm__page-head">
        <div className="adm__title-stack">
          <div className="meta">↗ ALL MINTED PASSPORTS</div>
          <h1 className="h1" style={{ fontSize: 36, margin: 0 }}>
            Passports
          </h1>
        </div>
        {can("dpp:create") && (
          <div className="row gap-16">
            <Button size="sm" onClick={() => navigate({ to: "/mint" })}>
              + Mint passport
            </Button>
          </div>
        )}
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          gap: 16,
          marginBottom: 24,
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1.4fr 1fr 1fr 1fr auto",
            gap: 12,
            flex: 1,
          }}
        >
          {[
            {
              label: "Search",
              content: (
                <Input
                  type="search"
                  placeholder="Serial, email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ padding: "10px 14px", fontSize: 13 }}
                />
              ),
            },
            {
              label: "Department",
              content: (
                <select
                  className="select"
                  value={departmentFilter}
                  style={{
                    padding: "10px 14px",
                    fontSize: 13,
                    appearance: "none",
                  }}
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                >
                  {["All departments", "phigora", "watchcentral"].map((o) => (
                    <option key={o}>{o}</option>
                  ))}
                </select>
              ),
            },
            {
              label: "Brand",
              content: (
                <select
                  className="select"
                  value={brandFilter}
                  onChange={(e) => setBrandFilter(e.target.value)}
                  style={{
                    padding: "10px 14px",
                    fontSize: 13,
                    appearance: "none",
                  }}
                >
                  {[
                    "All brands",
                    "Rolex",
                    "Patek Philippe",
                    "Omega",
                    "Audemars Piguet",
                    "Vacheron Constantin",
                  ].map((o) => (
                    <option key={o}>{o}</option>
                  ))}
                </select>
              ),
            },
            {
              label: "Status",
              content: (
                <select
                  className="select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  style={{
                    padding: "10px 14px",
                    fontSize: 13,
                    appearance: "none",
                  }}
                >
                  {[
                    "All statuses",
                    "Verified",
                    "Pending",
                    "Failed",
                    "Unknown",
                  ].map((o) => (
                    <option key={o}>{o}</option>
                  ))}
                </select>
              ),
            },
          ].map(({ label, content }) => (
            <div
              key={label}
              style={{ display: "flex", flexDirection: "column", gap: 5 }}
            >
              <label
                style={{
                  fontFamily: "var(--f-mono)",
                  fontSize: 10,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: "var(--ink-mute)",
                  fontWeight: 500,
                }}
              >
                {label}
              </label>
              {content}
            </div>
          ))}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 5,
              alignSelf: "end",
            }}
          >
            <Button
              variant="ghost"
              size="sm"
              style={{ height: 40 }}
              onClick={() => {
                setSearch("");
                setBrandFilter("All brands");
                setDepartmentFilter("All departments");
                setStatusFilter("All statuses");
              }}
            >
              Clear
            </Button>
          </div>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            alignSelf: "flex-end",
          }}
        >
          <span className="meta">SORT BY</span>
          <div className="seg">
            <button
              className={sortDir === "desc" ? "is-active" : ""}
              onClick={() => setSortDir("desc")}
            >
              Date ↓
            </button>
            <button
              className={sortDir === "asc" ? "is-active" : ""}
              onClick={() => setSortDir("asc")}
            >
              Date ↑
            </button>
          </div>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 18px",
          border: "1px solid var(--line)",
          borderBottom: 0,
          background: "var(--surface-2)",
          fontFamily: "var(--f-mono)",
          fontSize: 11,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: "var(--ink-mute)",
        }}
      >
        {loading ? (
          <span>Loading…</span>
        ) : error ? (
          <span style={{ color: "var(--danger)" }}>{error}</span>
        ) : (
          <span>
            <b style={{ color: "var(--ink)" }}>
              {data?.total.toLocaleString() ?? 0}
            </b>{" "}
            passports
            {(debouncedSearch ||
              brandFilter !== "All brands" ||
              departmentFilter !== "All departments" ||
              statusFilter !== "All statuses") && (
              <>
                {" "}
                · filtered to{" "}
                <b style={{ color: "var(--ink)" }}>{data?.items.length}</b>
              </>
            )}
          </span>
        )}
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead style={{ width: 32 }}>
              <input type="checkbox" />
            </TableHead>
            <TableHead>Watch</TableHead>
            <TableHead>Serial number</TableHead>
            <TableHead>Holder email</TableHead>
            <TableHead>Department</TableHead>
            <TableHead>Issued</TableHead>
            <TableHead>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell
                colSpan={7}
                style={{
                  textAlign: "center",
                  padding: 32,
                  color: "var(--ink-mute)",
                  fontFamily: "var(--f-mono)",
                  fontSize: 11,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                }}
              >
                Loading…
              </TableCell>
            </TableRow>
          ) : error ? (
            <TableRow>
              <TableCell
                colSpan={7}
                style={{
                  textAlign: "center",
                  padding: 32,
                  color: "var(--danger)",
                  fontFamily: "var(--f-mono)",
                  fontSize: 11,
                }}
              >
                {error}
              </TableCell>
            </TableRow>
          ) : data?.items.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={7}
                style={{
                  textAlign: "center",
                  padding: 32,
                  color: "var(--ink-mute)",
                  fontFamily: "var(--f-mono)",
                  fontSize: 11,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                }}
              >
                No passports found
              </TableCell>
            </TableRow>
          ) : (
            data?.items.map((p) => {
              const st = STATUS_UI[p.status];
              return (
                <TableRow
                  key={p.id}
                  style={{ cursor: "pointer" }}
                  onClick={() =>
                    navigate({
                      to: "/dpp",
                      search: {
                        brand: p.brand,
                        model: p.model,
                        serial: p.serialNumber,
                      },
                    })
                  }
                >
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" />
                  </TableCell>
                  <TableCell style={{ minWidth: 240 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                      }}
                    >
                      <div
                        style={{
                          width: 40,
                          height: 40,
                          flexShrink: 0,
                          border: "1px solid var(--line)",
                          background: "var(--surface-2)",
                          display: "grid",
                          placeItems: "center",
                          overflow: "hidden",
                        }}
                      >
                        {p.imageUrl ? (
                          <img
                            src={p.imageUrl}
                            alt=""
                            loading="lazy"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <span
                            style={{
                              fontFamily: "var(--f-mono)",
                              fontSize: 14,
                              color: "var(--ink-mute)",
                            }}
                          >
                            ⌚
                          </span>
                        )}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            fontFamily: "var(--f-head)",
                            fontWeight: 500,
                            color: "var(--ink)",
                            fontSize: 14,
                          }}
                        >
                          {p.brand} · {p.model}
                        </div>
                        <div
                          style={{
                            fontFamily: "var(--f-mono)",
                            fontSize: 10.5,
                            letterSpacing: "0.1em",
                            textTransform: "uppercase",
                            color: "var(--ink-mute)",
                            marginTop: 2,
                          }}
                        >
                          {p.department}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="mono">{p.serialNumber}</TableCell>
                  <TableCell>
                    <span className="mono">{p.customerEmail}</span>
                  </TableCell>
                  <TableCell
                    className="mono"
                    style={{
                      textTransform: "uppercase",
                      fontSize: 11,
                      letterSpacing: "0.1em",
                    }}
                  >
                    {p.department}
                  </TableCell>
                  <TableCell className="mono">
                    {fmtDate(p.createdAt)}
                  </TableCell>
                  <TableCell>
                    <Badge variant={st.variant}>{st.label}</Badge>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 18px",
          border: "1px solid var(--line)",
          borderTop: 0,
          background: "var(--surface-2)",
        }}
      >
        <span className="mono-cap text-mute">
          {data
            ? `Showing ${(page - 1) * LIMIT + 1} — ${Math.min(page * LIMIT, data.total)} of ${data.total.toLocaleString()}`
            : "Loading…"}
        </span>
        <div style={{ display: "flex", gap: 4 }}>
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{
              background: "transparent",
              border: "1px solid var(--line)",
              color: page === 1 ? "var(--ink-mute)" : "var(--ink-soft)",
              width: 32,
              height: 32,
              fontFamily: "var(--f-mono)",
              fontSize: 11,
              cursor: page === 1 ? "default" : "pointer",
            }}
          >
            ‹
          </button>
          {pages.map((p, i) =>
            p === "…" ? (
              <span
                key={`ellipsis-${i}`}
                style={{
                  width: 32,
                  height: 32,
                  display: "grid",
                  placeItems: "center",
                  fontFamily: "var(--f-mono)",
                  fontSize: 11,
                  color: "var(--ink-mute)",
                }}
              >
                …
              </span>
            ) : (
              <button
                key={p}
                onClick={() => setPage(p as number)}
                style={{
                  background: p === page ? "var(--accent)" : "transparent",
                  border: `1px solid ${p === page ? "var(--accent)" : "var(--line)"}`,
                  color: p === page ? "var(--accent-ink)" : "var(--ink-soft)",
                  width: 32,
                  height: 32,
                  fontFamily: "var(--f-mono)",
                  fontSize: 11,
                  cursor: "pointer",
                }}
              >
                {p}
              </button>
            )
          )}
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            style={{
              background: "transparent",
              border: "1px solid var(--line)",
              color:
                page === totalPages ? "var(--ink-mute)" : "var(--ink-soft)",
              width: 32,
              height: 32,
              fontFamily: "var(--f-mono)",
              fontSize: 11,
              cursor: page === totalPages ? "default" : "pointer",
            }}
          >
            ›
          </button>
        </div>
      </div>
    </div>
  );
}
